import React, {useCallback, useMemo, useRef} from 'react';

import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {useNewsletterDictionary} from 'src/hooks/dictionaries/useNewsletterDictionary';
import {sortNewsletterItems} from 'src/utils/sortNewsletterItems';

import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import FormSection from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {NewsletterSettingsSkeleton} from './newsletter-settings.skeleton';

import type {Dispatch, SetStateAction} from 'react';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {DictionaryTree} from 'src/openapi/admin/models/DictionaryTree';
import type {Error} from 'src/openapi/admin/models/Error';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    newsletterTypeCodes: string[],
    setNewsletterTypeCodes: Dispatch<SetStateAction<string[]>>,
    onFieldChange: (fieldName: string) => void,
    newsletterHistory?: React.ReactNode,
    disabled?: boolean,
    newsletterDictionary?: DictionaryTree[],
    availableNewsletterTypes?: string[],
};

const SECTION_NAME = 'Newsletter Settings';

export const NewslettersSettings: React.FC<Props> = props => {
    const {
        isLoading,
        loadingError,
        newsletterTypeCodes,
        setNewsletterTypeCodes,
        newsletterHistory,
        onFieldChange,
        disabled,
        newsletterDictionary: newsletterDictionaryProp,
        availableNewsletterTypes,
    } = props;
    const {newsletterDictionary: hookDictionary} = useNewsletterDictionary({skip: !!newsletterDictionaryProp});
    const newsletterDictionary = newsletterDictionaryProp ?? hookDictionary;

    const handleGroupToggle = useCallback((
        childIds: string[],
        defaultId: string,
        checked: boolean,
        label: string,
    ) => {
        onFieldChange(label);
        setNewsletterTypeCodes(prev => checked
            ? [...new Set([...prev, defaultId])]
            : prev.filter(c => !childIds.includes(c)),
        );
    }, [onFieldChange, setNewsletterTypeCodes]);

    const handleGroupChildSelect = useCallback((childIds: string[], code: string, label: string) => {
        onFieldChange(label);
        setNewsletterTypeCodes(prev => [
            ...prev.filter(c => !childIds.includes(c)),
            code,
        ]);
    }, [onFieldChange, setNewsletterTypeCodes]);

    const handleLeafToggle = useCallback((code: string, checked: boolean, label: string) => {
        onFieldChange(label);
        setNewsletterTypeCodes(prev => checked
            ? [...prev, code]
            : prev.filter(c => c !== code),
        );
    }, [onFieldChange, setNewsletterTypeCodes]);

    const comboboxGroupIndex = useMemo(() =>
        newsletterDictionary.findIndex(item => (item.children?.length ?? 0) > 1),
    [newsletterDictionary]);

    const lastComboValueRef = useRef<string>('');

    const newsletterOptions = useMemo(() => newsletterDictionary.flatMap((item, index) => {
        const children = availableNewsletterTypes
            ? item.children?.filter(child => availableNewsletterTypes.includes(child.id))
            : item.children;
        const childIds = children?.map(c => c.id) ?? [];

        if (childIds.length > 1 && index === comboboxGroupIndex) {
            const selectedChild = newsletterTypeCodes.find(c => childIds.includes(c));
            const defaultChildId = children![0].id;
            const comboValue = selectedChild ?? (lastComboValueRef.current || defaultChildId);
            if (selectedChild) {
                lastComboValueRef.current = selectedChild;
            }
            const sortedChildren = sortNewsletterItems(children!);
            return [(
                <Group direction="v" gap="xs" alignItems="start" key={item.id}>
                    <CheckBox
                        disabled={disabled}
                        label={item.description}
                        checked={!!selectedChild}
                        onCheckBoxChange={({event}: CheckBoxChangeData) =>
                            handleGroupToggle(childIds, comboValue, event.currentTarget.checked, item.description)}
                    />
                    <ComboBox
                        style={{width: 300}}
                        disabled={disabled || !selectedChild}
                        value={comboValue}
                        onChange={(code: string) =>
                            handleGroupChildSelect(childIds, code, code)}>
                        {sortedChildren.map(child => (
                            <ComboboxItem key={child.id} caption={child.description} value={child.id}/>
                        ))}
                    </ComboBox>
                </Group>
            )];
        }
        if (childIds.length) {
            const sortedChildren = sortNewsletterItems(children!);
            return (
                <Group key={item.id} style={{paddingTop: 16}} direction="v" gap="m"> {sortedChildren.map(child => (
                    <CheckBox
                        disabled={disabled}
                        name={child.id}
                        key={child.id}
                        label={child.description}
                        checked={newsletterTypeCodes.includes(child.id)}
                        onCheckBoxChange={({event}: CheckBoxChangeData) =>
                            handleLeafToggle(child.id, event.currentTarget.checked, child.description)}
                    />
                ))}
                </Group>
            );
        }
        if (availableNewsletterTypes && !availableNewsletterTypes.includes(item.id)) {
            return [];
        }
        return [(
            <CheckBox
                disabled={disabled}
                name={item.id}
                key={item.id}
                label={item.description}
                checked={newsletterTypeCodes.includes(item.id)}
                onCheckBoxChange={({event}: CheckBoxChangeData) =>
                    handleLeafToggle(item.id, event.currentTarget.checked, item.description)}
            />
        )];
    }), [newsletterDictionary, comboboxGroupIndex, newsletterTypeCodes, disabled,
        availableNewsletterTypes, handleGroupToggle, handleGroupChildSelect, handleLeafToggle]);

    return (
        <Skeleton shown={isLoading} view={<NewsletterSettingsSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                sectionName={SECTION_NAME}
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.USER_SCREEN_VIEW as AccessRight}>
                <FormSection headline={SECTION_NAME}>
                    <ResponsiveElementGroup>
                        <div style={{width: 300}}>
                            <Label>PitchBook newsletter</Label>
                            <Group direction="v" gap="xs" alignItems="start">
                                {newsletterOptions}
                            </Group>
                        </div>
                        {newsletterHistory}
                    </ResponsiveElementGroup>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
